import { Injectable, Injector, resolveForwardRef } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { HideConditionsService } from '@app/finxone-web-frontend/app/lib/services/hide-conditions-service/hide-conditions.service';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { getComponentByLabel } from '@app/finxone-web-frontend/app/lib/utils/get-component.utils';
import { buildHandleBarModel } from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  baseCssConfigStyle,
  formatNameAsUrl,
  isLayoutWidget,
  LayoutNames,
  NewUiZoneWidgetConfig,
  ProfileStateModel,
  UiPageConfig,
  UserInfoType,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { applyPageConfigStyles, createInjector, hideWidgetsIfNeededWithCallback } from '../utils';
import { ZonePageUrlProvider } from '../zone-url-provider';
import { ChildComponentList } from './main-widget/main-widget.component';

export type ComponentInjectorConfig = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: any;
  injector: Injector;
  widget: NewUiZoneWidgetConfig;
};

@Injectable({ providedIn: 'root' })
export class WidgetLayoutService {
  private footerHeightSubject = new BehaviorSubject<number>(0);
  public footerHeight$ = this.footerHeightSubject.asObservable();
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Select(ProfileState.getProfile)
  profile$!: Observable<ProfileStateModel>;
  private pageContentSubject = new BehaviorSubject<{
    style: baseCssConfigStyle;
    pageDetail: UiPageConfig | null;
  } | null>(null);
  public pageContent$ = this.pageContentSubject.asObservable();
  public injectorComponents: {
    [key: string]: ComponentInjectorConfig;
  } = {};
  private isWorkflowType: boolean;
  private currentPageDetail: UiPageConfig;
  private pageContentStyle: baseCssConfigStyle;
  private isModalPage: boolean;
  private urlParams: {
    zoneUrlInput: string;
    pageUrlInput: string;
    roleInput: string;
    subscriptionInput: string;
    idInput: string;
  };
  private profile: ProfileStateModel | undefined;
  private userInfo: UserInfoType;
  private pageIndex: number | undefined;
  public topPadding: string;
  public bottomPadding: string;

  constructor(
    private readonly configService: ConfigService,
    private readonly store: Store,
    private readonly countriesCurrenciesService: CountriesCurrenciesService,
    private readonly iconCacheService: IconCacheService,
    private readonly hideConditionsService: HideConditionsService,
    private readonly injector: Injector,
  ) {
    this.isWorkflowType = this.configService.getActiveConfigType()?.isWorkflow;
  }

  public initWidgetLayout(
    urlParams: {
      zoneUrlInput: string;
      pageUrlInput: string;
      roleInput: string;
      subscriptionInput: string;
      idInput: string;
    },
    route: ActivatedRoute,
    params: ParamMap,
    profile: ProfileStateModel,
    widgetLayoutType?: LayoutNames | undefined,
  ): void {
    this.urlParams = urlParams;
    this.profile = profile;
    // dataIn.userProfileData is fed from this field; it was never assigned, so every widget
    // saw an undefined profile. Keep it in step with the profile flowing through here.
    this.userInfo = profile as unknown as UserInfoType;
    const zoneUrlName = params.get('zone') ?? '';
    const pageUrlName = params.get('modal-page') ? params.get('modal-page') : params.get('page');
    this.isModalPage = !!params.get('modal-page');
    const id = params.get('id');

    this.configService
      .getPageUrlPageIndex(
        zoneUrlName ?? this.urlParams.zoneUrlInput,
        pageUrlName ?? this.urlParams.pageUrlInput,
      )
      .pipe(
        take(1),
        switchMap((pageIndex) => {
          if (pageIndex == null || pageIndex === -1) {
            console.error('Received invalid pageIndex:', pageIndex);
            return of(null);
          }

          this.pageIndex = pageIndex;
          const shouldLoadWithProfile =
            id == null && this.urlParams.zoneUrlInput === formatNameAsUrl(APP_ZONES.VERIFICATION);

          if (shouldLoadWithProfile) {
            return this.profile$.pipe(
              take(1),
              tap(() =>
                this.loadWidgetComponentsByLayoutType(this.urlParams, this.profile, widgetLayoutType),
              ),
            );
          } else {
            this.loadWidgetComponentsByLayoutType(this.urlParams, this.profile, widgetLayoutType);
            return of(null);
          }
        }),
      )
      .subscribe();

    if (this.urlParams?.zoneUrlInput === APP_ZONES.SIGNED_OUT_FIRST_INTRODUCTION) {
      localStorage.setItem('firstTime', 'true');
    }
  }

  /**
   * Get widgets based on the layout type.
   * @param pageConfig - Response object containing layout widgets
   * @param widgetLayoutType - Layout type to fetch widgets for
   * @returns Cloned array of widgets for the specified layout
   */
  public getWidgetsByLayoutType(
    pageConfig: UiPageConfig,
    widgetLayoutType?: LayoutNames,
  ): NewUiZoneWidgetConfig[] {
    if (!pageConfig) return [];

    switch (widgetLayoutType) {
      case LayoutNames.FOOTER_LAYOUT:
        return pageConfig?.footerWidgetLayouts || [];
      default:
        return pageConfig?.widgetLayouts || [];
    }
  }

  /**
   * Loads and builds widget components for the page based on URL parameters ,
   * user profile, and widgetLayout type.
   *
   * Applies hide conditions and returns only the visible widget components.
   */
  public loadWidgetComponentsByLayoutType(
    urlParams: {
      zoneUrlInput: string;
      pageUrlInput: string;
      roleInput: string;
      subscriptionInput: string;
      idInput: string;
    },
    profile: ProfileStateModel | undefined,
    widgetLayoutType?: LayoutNames | undefined,
  ): Observable<{ [key: string]: ComponentInjectorConfig }> {
    this.urlParams = urlParams;
    this.profile = profile;
    if (profile) this.userInfo = profile as unknown as UserInfoType;

    return this.configService.getCurrentRoleZoneWidgetsConfig(undefined, this.pageIndex).pipe(
      distinctUntilChanged(),
      switchMap((pageConfig: UiPageConfig) => {
        if (pageConfig) {
          this.currentPageDetail = pageConfig;
          this.updatePageContentStyle();
        }

        // Get widgets for the requested layout type
        const widgets = this.getWidgetsByLayoutType(pageConfig, widgetLayoutType);

        // Clear previous components
        this.destroyAllComponents();
        this.injectorComponents = {};

        // Set hide conditions for the current page
        this.hideConditionsService.extractAndSetCurrentPageWidgetsHideConditions(widgets);

        // Build the Handlebars model for hide condition evaluation
        return buildHandleBarModel(
          this.store,
          '',
          this.countriesCurrenciesService,
          this.iconCacheService,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          this.configService,
        ).pipe(
          take(1),
          map((modelToEvaluateAgainst) => {
            this.hideConditionsService.currentModelToCheck.set(modelToEvaluateAgainst);

            // Filter hidden widgets and create injector components
            hideWidgetsIfNeededWithCallback(modelToEvaluateAgainst, widgets, () => {
              this.syncInjectorComponents(widgets);
            });
            return this.injectorComponents;
          }),
        );
      }),
      takeUntil(this.destroy$),
    );
  }

  /**
   * Syncs the current injector components with a new widget list
   */
  private syncInjectorComponents(widgets: NewUiZoneWidgetConfig[]): void {
    const existingKeys = Object.keys(this.injectorComponents);
    const widgetKeys = new Set(widgets.map((w) => w.uniqueId));

    // Add or update components
    widgets.forEach((widget) => {
      const injectorComponent = this.createInjectorComponent(widget);
      if (injectorComponent) {
        this.injectorComponents[widget.uniqueId] = injectorComponent;
      }
    });

    // Remove widgets that no longer exist
    existingKeys.forEach((key) => {
      if (!widgetKeys.has(key)) {
        delete this.injectorComponents[key];
      }
    });
  }

  /**
   * Create a widget injector component entry.
   * @param widget - Widget configuration
   * @returns Object containing component, injector, widget, and layout type
   */
  private createInjectorComponent(widget: NewUiZoneWidgetConfig): ComponentInjectorConfig | null {
    const component = resolveForwardRef(getComponentByLabel(widget.name));
    if (!component) return null;

    return {
      component: component,
      injector: createInjector(
        widget,
        this.currentPageDetail,
        this.injector,
        this.configService,
        new ZonePageUrlProvider(
          this.urlParams.zoneUrlInput,
          this.urlParams.pageUrlInput,
          this.urlParams.roleInput,
          this.urlParams.subscriptionInput,
          this.urlParams.idInput,
        ),
        this.userInfo,
        this.profile,
        undefined,
        isLayoutWidget(widget.name)
          ? [
              {
                provide: ChildComponentList,
                useValue: new ChildComponentList(widget.childComponents ?? []),
              },
            ]
          : [],
      ),
      widget: widget,
    };
  }

  private updatePageContentStyle(): void {
    this.pageContentStyle = this.configService.getPageContentStyle(this.currentPageDetail.pageConfiguration);
    this.pageContentSubject.next({
      style: this.pageContentStyle,
      pageDetail: this.currentPageDetail,
    });

    if (this.currentPageDetail?.pageConfiguration) {
      const webAppElement = document.getElementById('web-app');
      if (webAppElement) {
        applyPageConfigStyles(webAppElement, this.currentPageDetail, this.isModalPage, this.isWorkflowType);
      }
    }
  }

  public setFooterLayoutHeight(height: number) {
    this.footerHeightSubject.next(height);
  }

  /**
   * Destroy all widget components
   */
  public destroyAllComponents() {
    Object.values(this.injectorComponents).forEach((componentRef) => {
      if (componentRef && componentRef.component.destroy) {
        componentRef.component.destroy();
      }
    });
    this.injectorComponents = {};
  }
}
